import React, { useState, useEffect } from "react";
import { Wallet, Plus, Edit3, EyeOff, Eye, Trash2 } from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { useApp } from "../../context/AppContext";
import { fundsService } from "../../services/chapterSettingsService";
import { Card } from "../ui/Card";
import { ActionButton } from "../ui/ActionButton";
import { FormInput } from "../ui/FormControls";

export const FundsPanel = ({ palette }) => {
  const { showMessage, activeFunds } = useApp();
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFund, setEditingFund] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [formName, setFormName] = useState("");
  const [formCode, setFormCode] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [saving, setSaving] = useState(false);

  const loadFunds = async () => {
    try {
      setLoading(true);
      const data = await fundsService.getAll();
      setFunds(data);
    } catch (err) {
      showMessage("Failed to load funds", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadFunds(); }, []);

  const resetForm = () => {
    setFormName("");
    setFormCode("");
    setFormDesc("");
    setEditingFund(null);
    setShowAdd(false);
  };

  const startEdit = (fund) => {
    setEditingFund(fund);
    setFormName(fund.name);
    setFormCode(fund.code);
    setFormDesc(fund.description || "");
    setShowAdd(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) { showMessage("Fund name is required", "error"); return; }
    if (!formCode.trim()) { showMessage("Fund code is required", "error"); return; }
    setSaving(true);
    try {
      if (editingFund) {
        await fundsService.update(editingFund.id, {
          name: formName.trim(),
          code: formCode.trim(),
          description: formDesc.trim(),
        });
        showMessage("Fund updated");
      } else {
        await fundsService.create({
          name: formName.trim(),
          code: formCode.trim().toUpperCase(),
          description: formDesc.trim(),
          isActive: true,
          sortOrder: funds.length,
        });
        showMessage("Fund created");
      }
      resetForm();
      await loadFunds();
    } catch (err) {
      showMessage("Failed to save fund: " + err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (fund) => {
    try {
      if (fund.isActive) {
        await fundsService.deactivate(fund.id);
        showMessage("Fund deactivated");
      } else {
        await fundsService.update(fund.id, { isActive: true });
        showMessage("Fund reactivated");
      }
      await loadFunds();
    } catch (err) {
      showMessage("Failed to update fund", "error");
    }
  };

  const handleDelete = async (fund) => {
    try {
      await fundsService.remove(fund.id);
      showMessage("Fund deleted", "warning");
      await loadFunds();
    } catch (err) {
      showMessage(err.message || "Failed to delete fund", "error");
    }
  };

  const th = {
    padding: "8px 12px",
    textAlign: "left",
    fontSize: "0.65rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: palette.text3,
    borderBottom: `2px solid ${palette.cardBorder}`,
  };

  const td = {
    padding: "7px 12px",
    fontSize: FONT_SIZES.xs,
    fontFamily: FONT_FAMILY,
    borderBottom: `1px solid ${palette.cardBorder}`,
  };

  return (
    <>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Wallet size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
            <span style={{ fontSize: FONT_SIZES.md, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
              Funds
            </span>
            <span style={{ fontSize: FONT_SIZES.xs, color: palette.text3, fontFamily: FONT_FAMILY }}>
              ({funds.length} total, {funds.filter((f) => f.isActive).length} active)
            </span>
          </div>
          {!showAdd && (
            <ActionButton label="Add Fund" icon={Plus} small onClick={() => { resetForm(); setShowAdd(true); }} />
          )}
        </div>

        {/* Add/Edit form */}
        {showAdd && (
          <div style={{
            padding: 16, marginBottom: 16, borderRadius: 8,
            border: `2px solid ${palette.accent1}30`, background: `${palette.accent1}06`,
          }}>
            <div style={{ fontSize: FONT_SIZES.xs, fontWeight: 700, color: palette.accent1, fontFamily: FONT_FAMILY, marginBottom: 10, textTransform: "uppercase" }}>
              {editingFund ? "Edit Fund" : "New Fund"}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10, marginBottom: 10 }}>
              <FormInput label="Fund Name" value={formName} onChange={setFormName} placeholder="e.g. General Fund" required />
              <FormInput label="Fund Code" value={formCode} onChange={setFormCode} placeholder="e.g. GENERAL" required />
            </div>
            <FormInput label="Description" value={formDesc} onChange={setFormDesc} placeholder="Optional description" />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <ActionButton label="Cancel" variant="secondary" small onClick={resetForm} />
              <ActionButton label={saving ? "Saving..." : editingFund ? "Update" : "Create"} small onClick={handleSave} disabled={saving || !formName.trim() || !formCode.trim()} />
            </div>
          </div>
        )}

        {/* Fund table */}
        {loading ? (
          <div style={{ textAlign: "center", padding: 20, color: palette.text3, fontFamily: FONT_FAMILY, fontSize: FONT_SIZES.xs }}>
            Loading funds...
          </div>
        ) : funds.length === 0 ? (
          <div style={{ textAlign: "center", padding: 30, color: palette.text3, fontFamily: FONT_FAMILY, fontSize: FONT_SIZES.sm }}>
            No funds yet. Add your first fund to get started.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
            <thead>
              <tr>
                <th style={th}>Name</th>
                <th style={th}>Code</th>
                <th style={th}>Description</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {funds.map((fund) => (
                <tr key={fund.id} style={{ opacity: fund.isActive ? 1 : 0.5 }}>
                  <td style={{ ...td, fontWeight: 600, color: palette.text1 }}>{fund.name}</td>
                  <td style={{ ...td, color: palette.text2 }}>
                    <span style={{
                      fontSize: "0.65rem", fontWeight: 600, padding: "2px 6px", borderRadius: 4,
                      background: `${palette.accent1}10`, color: palette.accent1, fontFamily: FONT_FAMILY,
                    }}>
                      {fund.code}
                    </span>
                  </td>
                  <td style={{ ...td, color: palette.text3, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {fund.description || "—"}
                  </td>
                  <td style={td}>
                    <span style={{
                      fontSize: "0.6rem", fontWeight: 600, padding: "2px 6px", borderRadius: 8,
                      background: fund.isActive ? "#dcfce7" : "#f3f4f6",
                      color: fund.isActive ? "#16a34a" : "#6b7280",
                    }}>
                      {fund.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                      <button onClick={() => startEdit(fund)} title="Edit"
                        style={{ background: "none", border: "none", cursor: "pointer", color: palette.text3, padding: 3, display: "flex" }}>
                        <Edit3 size={ICON_SIZES.sm} />
                      </button>
                      <button onClick={() => handleToggleActive(fund)} title={fund.isActive ? "Deactivate" : "Reactivate"}
                        style={{ background: "none", border: "none", cursor: "pointer", color: palette.text3, padding: 3, display: "flex" }}>
                        {fund.isActive ? <EyeOff size={ICON_SIZES.sm} /> : <Eye size={ICON_SIZES.sm} />}
                      </button>
                      <button onClick={() => handleDelete(fund)} title="Delete"
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", padding: 3, display: "flex" }}>
                        <Trash2 size={ICON_SIZES.sm} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card>
        <div style={{ fontSize: FONT_SIZES.xs, color: palette.text3, fontFamily: FONT_FAMILY, lineHeight: 1.6 }}>
          <strong>About Funds:</strong> Funds categorize gifts by purpose or campaign.
          Active funds appear in gift entry dropdowns. Deactivated funds are hidden from new entries
          but remain on existing gifts. Deleting a fund is only possible if no gifts reference it —
          deactivate instead if gifts exist.
        </div>
      </Card>
    </>
  );
};
